import React, {useEffect, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import SectionHeading from "../../../../../components/SectionHeading";
import {PencilIcon, PlusIcon, SaveIcon, Trash2Icon} from "lucide-react";
import {CustomFieldRequest, FieldGroup, FieldType, TournamentCustomField} from "../../../../../types/tournament";
import {normalizePage} from "../../../../../types/page";
import {useCategories} from "../../../../../hooks/useTournaments";

type OutletCtx = { tournamentId: string | null; isEdit?: boolean };

const GROUPS: FieldGroup[] = [
    "CUSTOM",
    "PERSONAL_DETAILS",
    "CONTACT_DETAILS",
    "ADDRESS",
    "MEDICAL_AID",
    "MEDICAL_INFO",
    "EMERGENCY_CONTACT",
    "RACE_DETAILS",
    "REGISTRATION_DETAILS",
    "TEAM_DETAILS",
    "DIETARY_REQUIREMENTS",
    "APPAREL",
    "ACCOMMODATION",
    "TRANSPORT",
    "EXPERIENCE",
    "CONSENT",
    "PAYMENT_DETAILS",
];
const TYPES: FieldType[] = ["TEXT", "TEXTAREA", "NUMBER", "DATE", "DROPDOWN", "BOOLEAN", "CHECKBOX", "DOCUMENT", "IMAGE"];

const GROUP_LABELS: Record<FieldGroup, string> = {
    CUSTOM: "Custom",
    PERSONAL_DETAILS: "Personal details",
    CONTACT_DETAILS: "Contact details",
    ADDRESS: "Address",
    MEDICAL_AID: "Medical aid",
    MEDICAL_INFO: "Medical info",
    EMERGENCY_CONTACT: "Emergency contact",
    RACE_DETAILS: "Race details",
    REGISTRATION_DETAILS: "Registration details",
    TEAM_DETAILS: "Team details",
    DIETARY_REQUIREMENTS: "Dietary requirements",
    APPAREL: "Apparel",
    ACCOMMODATION: "Accommodation",
    TRANSPORT: "Transport",
    EXPERIENCE: "Experience",
    CONSENT: "Consent",
    PAYMENT_DETAILS: "Payment details",
};

const TYPE_LABELS: Record<FieldType, string> = {
    TEXT: "Text",
    TEXTAREA: "Long text",
    NUMBER: "Number",
    DATE: "Date",
    DROPDOWN: "Dropdown",
    BOOLEAN: "Yes / No",
    CHECKBOX: "Checkbox",
    DOCUMENT: "File upload",
    IMAGE: "Image upload",
};

type FieldForm = {
    id?: string;        // present when editing an existing field
    position?: number;  // preserved on edit
    section: FieldGroup;
    label: string;
    description: string;
    type: FieldType;
    required: boolean;
    options: string;
    // Empty = collected for all play categories.
    categoryIds: string[];
};

function emptyField(): FieldForm {
    return {section: "CUSTOM", label: "", description: "", type: "TEXT", required: true, options: "", categoryIds: []};
}

function fieldToForm(f: TournamentCustomField): FieldForm {
    return {
        id: f.id,
        position: f.position,
        section: f.section,
        label: f.label,
        description: f.description ?? "",
        type: f.type,
        required: Boolean(f.required),
        options: f.options ?? "",
        categoryIds: f.categoryIds ?? [],
    };
}

export function DataCollectionStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const tournamentId =
        mode === "edit"
            ? (params.tournamentId ?? outlet.tournamentId ?? null)
            : (outlet.tournamentId ?? sp.get("tournamentId") ?? null);

    const [loading, setLoading] = useState(false);
    const [savingField, setSavingField] = useState(false);
    const [fields, setFields] = useState<TournamentCustomField[]>([]);
    const [fieldEditor, setFieldEditor] = useState<FieldForm | null>(null);

    const {data: categories} = useCategories(businessId ?? "", tournamentId);
    const categoryList = categories ?? [];
    const categoryName = (id: string) => categoryList.find((c) => c.id === id)?.name ?? "Unknown";

    const loadFields = async () => {
        if (!businessId || !tournamentId) return;
        try {
            const {data} = await api.get(`/api/v2/business/${businessId}/tournaments/${tournamentId}/custom-fields`);
            // Endpoint returns a Spring Page → normalize to a flat list.
            const list = normalizePage<TournamentCustomField>(data).items;
            list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
            setFields(list);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load custom fields"));
        }
    };

    useEffect(() => {
        if (!businessId || !tournamentId) return;
        setLoading(true);
        loadFields().finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, tournamentId]);

    const saveField = async (f: FieldForm) => {
        if (!businessId || !tournamentId) return;
        if (!f.label.trim()) {
            notify.error("Field label is required.");
            return;
        }

        const isEditing = !!f.id;
        setSavingField(true);
        try {
            const payload: CustomFieldRequest = {
                label: f.label.trim(),
                description: f.description || null,
                type: f.type,
                section: f.section,
                options: f.type === "DROPDOWN" ? (f.options ?? "") : null,
                required: Boolean(f.required),
                position: isEditing ? (f.position ?? 0) : fields.length + 1,
                categoryIds: f.categoryIds,
            };

            const url = `/api/v2/business/${businessId}/tournaments/${tournamentId}/custom-fields`;
            if (isEditing) {
                await api.put(`${url}/${f.id}`, payload);
            } else {
                await api.post(url, payload);
            }
            await loadFields();
            notify.success(isEditing ? "Field updated" : "Field added");
            setFieldEditor(null);
        } catch (e) {
            notify.error(getErrMsg(e, isEditing ? "Failed to update field" : "Failed to add field"));
        } finally {
            setSavingField(false);
        }
    };

    const deleteField = async (f: TournamentCustomField) => {
        if (!businessId || !tournamentId) return;
        if (!window.confirm(`Delete field "${f.label}"?`)) return;
        try {
            await api.delete(`/api/v2/business/${businessId}/tournaments/${tournamentId}/custom-fields/${f.id}`);
            setFields((prev) => prev.filter((x) => x.id !== f.id));
            notify.success("Field deleted");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete field"));
        }
    };

    const goNext = () => {
        if (!tournamentId) return;
        if (mode === "edit") navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/review`);
        else navigate(`/business/tournaments/new/review?tournamentId=${encodeURIComponent(tournamentId)}`);
    };

    if (!tournamentId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Details</span> first.
            </div>
        );
    }

    const inputCls =
        "mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none";

    // ---- Field editor view
    if (fieldEditor) {
        const needsOptions = fieldEditor.type === "DROPDOWN";
        const canSave = fieldEditor.label.trim().length > 0 && !savingField;

        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{fieldEditor.id ? "Edit field" : "New field"}</h2>
                        <p className="mt-1 text-sm text-gray-600">Define what competitors must provide during
                            registration.</p>
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" variant="secondary" size="md" onClick={() => setFieldEditor(null)}
                                disabled={savingField}>
                            Cancel
                        </Button>
                        <Button type="button" variant="primary" size="md" onClick={() => saveField(fieldEditor)}
                                disabled={!canSave}>
                            <span className="inline-flex items-center gap-2">
                                <SaveIcon className="size-4"/>{savingField ? "Saving…" : (fieldEditor.id ? "Update field" : "Save field")}
                            </span>
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <div className="text-sm font-medium text-gray-900">Group</div>
                        <select
                            value={fieldEditor.section}
                            onChange={(e) => setFieldEditor({...fieldEditor, section: e.target.value as FieldGroup})}
                            className={inputCls}
                        >
                            {GROUPS.map((g) => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <div className="text-sm font-medium text-gray-900">Label *</div>
                        <input
                            value={fieldEditor.label}
                            onChange={(e) => setFieldEditor({...fieldEditor, label: e.target.value})}
                            placeholder="Name of field (e.g. T-shirt size)"
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <div className="text-sm font-medium text-gray-900">Type</div>
                        <select
                            value={fieldEditor.type}
                            onChange={(e) => setFieldEditor({...fieldEditor, type: e.target.value as FieldType})}
                            className={inputCls}
                        >
                            {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <div className="text-sm font-medium text-gray-900">Description</div>
                        <input
                            value={fieldEditor.description}
                            onChange={(e) => setFieldEditor({...fieldEditor, description: e.target.value})}
                            placeholder="Shown under the field (optional)"
                            className={inputCls}
                        />
                    </div>

                    <div
                        className="md:col-span-3 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Required</div>
                            <div className="text-xs text-gray-600">Competitors must fill this in to proceed.</div>
                        </div>
                        <input
                            type="checkbox"
                            checked={fieldEditor.required}
                            onChange={(e) => setFieldEditor({...fieldEditor, required: e.target.checked})}
                            className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                        />
                    </div>

                    <div className="md:col-span-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm font-semibold text-gray-900">Visible for play categories</div>
                        <div className="mt-0.5 text-xs text-gray-600">
                            Choose which categories collect this field. Leave all unselected to collect it for{" "}
                            <span className="font-medium">every</span> category.
                        </div>
                        {categoryList.length === 0 ? (
                            <p className="mt-3 text-xs text-gray-500">
                                No play categories yet — this field will apply to all categories.
                            </p>
                        ) : (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {[...categoryList].sort((a, b) => a.position - b.position).map((c) => {
                                    const selected = fieldEditor.categoryIds.includes(c.id);
                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() =>
                                                setFieldEditor((prev) =>
                                                    prev
                                                        ? {
                                                            ...prev,
                                                            categoryIds: selected
                                                                ? prev.categoryIds.filter((id) => id !== c.id)
                                                                : [...prev.categoryIds, c.id],
                                                        }
                                                        : prev
                                                )
                                            }
                                            className={
                                                "rounded-full px-3 py-1 text-xs font-semibold ring-1 transition-colors " +
                                                (selected
                                                    ? "bg-orange-700 text-white ring-orange-600"
                                                    : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50")
                                            }
                                        >
                                            {c.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {fieldEditor.categoryIds.length > 0 ? (
                            <button
                                type="button"
                                onClick={() => setFieldEditor((prev) => (prev ? {...prev, categoryIds: []} : prev))}
                                className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                            >
                                Clear — collect for all categories
                            </button>
                        ) : null}
                    </div>

                    {needsOptions ? (
                        <div className="md:col-span-3">
                            <div className="text-sm font-medium text-gray-900">Options</div>
                            <div className="mt-1 text-xs text-gray-500">Comma-separated values (e.g. Small, Medium,
                                Large)
                            </div>
                            <input
                                value={fieldEditor.options}
                                onChange={(e) => setFieldEditor({...fieldEditor, options: e.target.value})}
                                placeholder="e.g. Small, Medium, Large"
                                className={inputCls}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    // ---- main view
    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit data collection" : "Set up data collection"}
                description="Add custom fields competitors must fill in when registering."
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading…</div>
            ) : (
                <>
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div className="text-sm font-semibold text-gray-900">Always collected</div>
                        <p className="mt-1 text-sm text-gray-600">
                            We always collect the competitor's <span className="font-semibold">name</span>,{" "}
                            <span className="font-semibold">email</span> and{" "}
                            <span className="font-semibold">phone number</span>. Use custom fields below to collect
                            anything else.
                        </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div
                            className="border-b border-gray-200 bg-gray-50 px-5 py-4 flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Custom fields</div>
                                <div className="mt-1 text-xs text-gray-600">Add fields competitors must fill in.</div>
                            </div>
                            <Button type="button" variant="primary" size="md"
                                    onClick={() => setFieldEditor(emptyField())}>
                                <span className="inline-flex items-center gap-2"><PlusIcon className="size-4"/>Add field</span>
                            </Button>
                        </div>

                        <ul className="divide-y divide-gray-100">
                            {fields.length === 0 ? (
                                <li className="px-5 py-10 text-center text-sm text-gray-500">
                                    No custom fields yet. Click <span className="font-medium">Add field</span>.
                                </li>
                            ) : fields.map((f) => (
                                <li key={f.id} className="px-5 py-4 hover:bg-gray-50/60">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <div
                                                    className="truncate text-sm font-semibold text-gray-900">{f.label}</div>
                                                <span
                                                    className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200">
                                                    {GROUP_LABELS[f.section] ?? f.section}
                                                </span>
                                                <span
                                                    className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-100">
                                                    {TYPE_LABELS[f.type] ?? f.type}
                                                </span>
                                                {f.required ? (
                                                    <span
                                                        className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700 ring-1 ring-orange-100">
                                                        Required
                                                    </span>
                                                ) : null}
                                            </div>
                                            {f.description ? <div
                                                className="mt-1 line-clamp-1 text-xs text-gray-500">{f.description}</div> : null}
                                            {f.type === "DROPDOWN" && f.options ? (
                                                <div className="mt-1 text-xs text-gray-500">Options: {f.options}</div>
                                            ) : null}
                                            <div className="mt-1 text-xs text-gray-500">
                                                {f.categoryIds && f.categoryIds.length > 0
                                                    ? `Categories: ${f.categoryIds.map(categoryName).join(", ")}`
                                                    : "All categories"}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setFieldEditor(fieldToForm(f))}
                                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                            >
                                                <PencilIcon className="size-4"/>
                                                <span className="hidden sm:inline">Edit</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => deleteField(f)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2Icon className="size-4"/>
                                                <span className="hidden sm:inline">Delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex items-center justify-end gap-3">
                        <Button type="button" variant="secondary" size="md"
                                onClick={() => navigate("/business/tournaments")}>
                            Exit
                        </Button>
                        <Button type="button" variant="primary" size="md" onClick={goNext}>
                            Continue to Review
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
